import { Fragment, useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import "./Header.scss";

import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { UserProfile } from "./UserProfile";
import { AuthService } from "../../Modules/Auth/services/AuthService";
import { Language } from "./Language";
import { NavLink as RouterNavLink, NavLinkProps } from "react-router-dom";
import { UtilService } from "../Utils/UtilService";

interface CustomNavLinkProps extends NavLinkProps {
  activeClassName?: string;
  inactiveClassName?: string;
}

export const CustomNavLink: React.FC<CustomNavLinkProps> = ({
  activeClassName = "text-yellow-500",
  inactiveClassName = "text-white",
  className = "",
  children,
  ...props
}) => {
  return (
    <RouterNavLink
      {...props}
      className={({ isActive }) =>
        `${className} text-base font-medium ${
          isActive ? activeClassName : inactiveClassName
        } hover:text-yellow-500`
      }
    >
      {children}
    </RouterNavLink>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const currentUser = AuthService.getLocalUser();
  const logoutAction = () => AuthService.logout();

  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  const focusPanel = true;
  let allowedMain = AuthService.isManagementAllowed();

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-blue-700 text-white">
        <Popover className="relative px-0 mx-0">
          <div className="px-12">
            <div className="flex items-center justify-between py-5 md:justify-start md:space-x-10">
              <div className="flex justify-start lg:w-0 lg:flex-1">
                <CustomNavLink to={"/"}>
                  <Logo height={40} width={40} className=""/>
                </CustomNavLink>
              </div>
              <div className="-my-2 -mr-2 md:hidden">
                <PopoverButton className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                  <span className="sr-only">Open menu</span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </PopoverButton>
              </div>
              <PopoverGroup as="nav" className="hidden space-x-10 md:flex">
                <CustomNavLink to={"/home"}> {t("home")}</CustomNavLink>
                <CustomNavLink
                  to={
                    allowedMain
                      ? "/services/accounts/home"
                      : "/services/accounts/companies"
                  }
                >
                  {t("services")}
                </CustomNavLink>
                <CustomNavLink to={"/about"}>{t("about")}</CustomNavLink>
              </PopoverGroup>
              <div className="hidden items-center justify-end md:flex md:flex-1 lg:w-0">
                {!currentUser ? (
                  <span>
                    <CustomNavLink to={"/auth/login"}>
                      {t("signIn")}
                    </CustomNavLink>
                    <NavLink
                      to="/auth/register"
                      className="ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-base font-medium shadow-sm hover:bg-yellow-700 hover:text-white"
                    >
                      {t("signUp")}
                    </NavLink>
                  </span>
                ) : (
                  <UserProfile />
                )}
                <div className="px-5 relative">
                  <Language />
                </div>
                <button
                  onClick={toggleFullscreen}
                  className="p-2 hover:bg-gray-100 rounded"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <Transition
            as={Fragment}
            enter="duration-200 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="duration-100 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <PopoverPanel
              focus={focusPanel}
              className="bg absolute inset-x-0 top-0 origin-top-right transform transition md:hidden"
            >
              {({ close }) => (
                <div className="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="px-5 pt-5 pb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Logo height={40} width={40} className="" />
                      </div>
                      <div className="mr-2">
                        <PopoverButton className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                          <span className="sr-only">{t('close_menu')}</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </PopoverButton>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6 py-6 px-5">
                    {!currentUser ? (
                      <div>
                        <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                          <CustomNavLink to={"/home"} onClick={() => close()}>
                            {t("home")}
                          </CustomNavLink>
                        </div>

                        <div className="py-2">
                          <NavLink
                          className="text-gray-500 text-base font-medium"
                            to={
                              allowedMain
                                ? "/services/accounts/home"
                                : "/services/accounts/companies"
                            }
                            onClick={() => close()}
                          >
                            {t('companies')}
                          </NavLink>
                        </div>

                        <div className="py-2">
                          <CustomNavLink to="/about" onClick={() => close()}>
                            {t('about')}
                          </CustomNavLink>
                        </div>
                        <div className="py-2">
                          <NavLink
                            to="/auth/register"
                            className="flex w-full items-center justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700"
                            onClick={() => close()}
                          >
                            {t('signup')}
                          </NavLink>
                          <p className="mt-6 text-center text-base font-medium text-gray-500">
                            {t('signin_prompt_1')}{" "}
                            <CustomNavLink
                              className="text-blue-800"
                              to="/auth/login"
                              onClick={() => close()}
                            >
                             {t('signin_prompt_2')}
                            </CustomNavLink>
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="">
                        <div className="py-2">
                          <NavLink
                            to="/services/settings/account"
                            onClick={() => close()}
                          >
                            {currentUser?.user
                              ? `${UtilService.titleCase(
                                  currentUser?.user?.firstName ?? ""
                                )} ${UtilService.titleCase(
                                  currentUser?.user?.lastName ?? ""
                                )}`
                              : t('add_profile')}
                          </NavLink>
                        </div>

                        <div className="py-2">
                          <NavLink
                            to="/services/settings/account"
                            onClick={() => close()}
                          >
                            {t('settings')}
                          </NavLink>
                        </div>

                        <div className="py-2">
                          <NavLink
                            to={
                              allowedMain
                                ? "/services/accounts/home"
                                : "/services/accounts/companies"
                            }
                            onClick={() => close()}
                          >
                            {t('companies')}
                          </NavLink>
                        </div>

                        <div className="py-2">
                          <div
                            className="cursor-pointer"
                            onClick={logoutAction}
                          >
                            {t('signout')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
    </>
  );
};
